/**
 * Contracts for module "audit-analysis". Owner: audit (Phase 1, Step 0).
 * The AI analyst output that the paid Growth Audit report (PDF + web) and the portal read.
 * Produced by the `analysis` pipeline stage (Track A), grounded in Quixo Brain context (Track B),
 * fed by evidence from the crawl/GBP/visibility/competitor stages (Track C), rendered by Track D.
 * Import as '@qm/contracts/audit-analysis'.
 *
 * Rules:
 * - Exactly six signals, ids and names as sold on the website.
 * - Anything not measured is score null + grade 'not_measured' - never estimated. A channel that was checked and
 *   does not exist (e.g. no website for a social-only business) is measured as absent (score 0), not 'not_measured'.
 * - Subject kind 'social' covers creators/influencers and businesses without a website; same six signals.
 * - Every finding cites evidence (evidenceRefs/checkIds); plan entries reference finding ids.
 * - Customer-facing text is bilingual: `en` required, `ar` written (not machine-translated) or null
 *   until reviewed. The QA gate holds Arabic deliveries whose text is missing.
 */
import { z } from 'zod';

export const AUDIT_ANALYSIS_VERSION = 1 as const;

// ---------- the six promised signals ----------
export const SIGNAL_IDS = ['search', 'local', 'website', 'social', 'content', 'reputation'] as const;
export type SignalId = (typeof SIGNAL_IDS)[number];
export const SignalIdSchema = z.enum(SIGNAL_IDS);

/** Display names exactly as on quixomarketing.com. */
export const SIGNAL_NAMES: Record<SignalId, { en: string; ar: string }> = {
  search: { en: 'Search visibility', ar: 'الظهور في البحث' },
  local: { en: 'Local presence', ar: 'الحضور المحلي' },
  website: { en: 'Website experience', ar: 'تجربة الموقع' },
  social: { en: 'Social media', ar: 'وسائل التواصل الاجتماعي' },
  content: { en: 'Content and messaging', ar: 'المحتوى والرسائل' },
  reputation: { en: 'Reputation and trust', ar: 'السمعة والثقة' },
};

// ---------- shared primitives ----------
/** Bilingual customer-facing text. `ar` null = not yet written/reviewed. */
export const LocalizedTextSchema = z.object({
  en: z.string().trim().min(1).max(4000),
  ar: z.string().trim().min(1).max(4000).nullable(),
});
export type LocalizedText = z.infer<typeof LocalizedTextSchema>;

export const Score = z.number().int().min(0).max(100);
export const GRADES = ['A', 'B', 'C', 'D', 'F', 'not_measured'] as const;
export const GradeSchema = z.enum(GRADES);
export type Grade = z.infer<typeof GradeSchema>;

/** Single grading rule used by analyst, renderer and portal. */
export function gradeFor(score: number | null): Grade {
  if (score === null) return 'not_measured';
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 55) return 'C';
  if (score >= 40) return 'D';
  return 'F';
}

export const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
export const SeveritySchema = z.enum(SEVERITIES);
export type Severity = z.infer<typeof SeveritySchema>;

export const FIX_OWNERS = ['business_owner', 'web_developer', 'marketing', 'quixo'] as const;
export const FixOwnerSchema = z.enum(FIX_OWNERS);

export const EFFORTS = ['hours', 'days', 'weeks'] as const;
export const EffortSchema = z.enum(EFFORTS);

const Id = z.string().trim().min(1).max(64).regex(/^[a-z0-9][a-z0-9_.:-]*$/, 'lowercase id');
const Ref = z.string().trim().min(1).max(200);

// ---------- signals ----------
export const SignalAssessmentSchema = z.object({
  id: SignalIdSchema,
  score: Score.nullable(),
  grade: GradeSchema,
  headline: LocalizedTextSchema,
  whatWeFound: LocalizedTextSchema,
  whyItMatters: LocalizedTextSchema,
  /** Evidence keys / check ids backing this assessment (e.g. 'gbp.reviews', 'check:title_missing'). */
  evidenceRefs: z.array(Ref).max(50),
}).superRefine((s, ctx) => {
  if (s.grade !== gradeFor(s.score)) {
    ctx.addIssue({ code: 'custom', path: ['grade'], message: `grade must be ${gradeFor(s.score)} for score ${s.score}` });
  }
});
export type SignalAssessment = z.infer<typeof SignalAssessmentSchema>;

// ---------- findings ----------
export const FindingSchema = z.object({
  id: Id,
  signal: SignalIdSchema,
  severity: SeveritySchema,
  title: LocalizedTextSchema,
  /** What was observed, quoting measured evidence. */
  evidence: LocalizedTextSchema,
  /** Business impact for the owner (bookings, enquiries, trust). */
  impact: LocalizedTextSchema,
  /** One-paragraph fix. */
  fix: LocalizedTextSchema,
  steps: z.array(LocalizedTextSchema).min(1).max(12),
  owner: FixOwnerSchema,
  effort: EffortSchema,
  /** Deterministic check ids from the audit engine that triggered or support this finding. */
  checkIds: z.array(Ref).max(30),
  /** Quixo Brain row refs used (e.g. 'bw:healthcare/protected_title_without_holder'). */
  brainRefs: z.array(Ref).max(30),
});
export type Finding = z.infer<typeof FindingSchema>;

// ---------- plan ----------
export const PlanSchema = z.object({
  quickWins: z.array(Id).max(15),
  days30: z.array(Id).max(25),
  days90: z.array(Id).max(40),
});
export type Plan = z.infer<typeof PlanSchema>;

// ---------- competitors (scored on the SAME checks) ----------
export const COMPETITOR_SOURCES = ['places', 'search', 'customer', 'model'] as const;
export const CompetitorAnalysisSchema = z.object({
  name: z.string().trim().min(1).max(200),
  domain: z.string().trim().toLowerCase().max(253).nullable(),
  source: z.enum(COMPETITOR_SOURCES),
  overall: Score.nullable(),
  signals: z.object(Object.fromEntries(SIGNAL_IDS.map((id) => [id, Score.nullable()])) as Record<SignalId, z.ZodNullable<typeof Score>>),
  /** Handle-level metrics for social benchmarks, e.g. { followers, posts, followersPerPost }. */
  metrics: z.record(z.string().max(60), z.union([z.number(), z.string().max(200), z.null()])).default({}),
  /** Where they beat the buyer, short. */
  edge: LocalizedTextSchema.nullable(),
});
export type CompetitorAnalysis = z.infer<typeof CompetitorAnalysisSchema>;

// ---------- subject (website audit vs social-only / creator audit) ----------
export const SUBJECT_KINDS = ['website', 'social'] as const;
export const SOCIAL_PLATFORMS = ['instagram', 'facebook', 'tiktok', 'threads', 'youtube', 'linkedin', 'x', 'snapchat', 'google_business'] as const;
export const SubjectSchema = z.object({
  kind: z.enum(SUBJECT_KINDS),
  businessName: z.string().trim().min(1).max(200),
  /** Audited site for 'website'; for 'social' the owned domain if one exists, else null (absence is itself a finding). */
  domain: z.string().trim().toLowerCase().max(253).nullable(),
  profiles: z.array(z.object({ platform: z.enum(SOCIAL_PLATFORMS), handle: z.string().max(200), url: z.string().url().max(500) })).max(10),
  sector: z.string().max(64).nullable(),
  city: z.string().max(100).nullable(),
});
export type Subject = z.infer<typeof SubjectSchema>;

// ---------- channel sweep (every channel, present or not) ----------
export const CHANNEL_STATUSES = ['active', 'dormant', 'broken', 'misbranded', 'duplicate', 'absent', 'not_measured'] as const;
export const ChannelSchema = z.object({
  channel: z.string().trim().min(1).max(60),
  status: z.enum(CHANNEL_STATUSES),
  url: z.string().max(500).nullable(),
  /** Captured public metrics, e.g. { followers: 4987, posts: 502, daysSinceLastPost: 18 }. */
  metrics: z.record(z.string().max(60), z.union([z.number(), z.string().max(200), z.null()])),
  whatWeFound: LocalizedTextSchema,
  severity: SeveritySchema.nullable(),
});
export type Channel = z.infer<typeof ChannelSchema>;

// ---------- baseline -> 90-day KPI targets ----------
export const KpiTargetSchema = z.object({
  metric: LocalizedTextSchema,
  /** Baseline value as captured; null = not measured today (target may be "measured"). */
  baseline: z.union([z.number(), z.string().max(100), z.null()]),
  target: z.union([z.number(), z.string().max(100)]),
  signal: SignalIdSchema,
});
export type KpiTarget = z.infer<typeof KpiTargetSchema>;

// ---------- coverage + QA ----------
export const CoverageSchema = z.object({
  /** 0-1 share of planned checks that produced measured evidence. */
  overall: z.number().min(0).max(1),
  bySignal: z.object(Object.fromEntries(SIGNAL_IDS.map((id) => [id, z.number().min(0).max(1)])) as Record<SignalId, z.ZodNumber>),
  pagesFetched: z.number().int().min(0),
  notMeasured: z.array(z.object({ ref: Ref, reason: z.string().max(200) })).max(100),
});
export type Coverage = z.infer<typeof CoverageSchema>;

export const QA_STATUSES = ['pass', 'hold'] as const;
export const QA_HOLD_REASONS = [
  'low_coverage', 'fallback_used', 'profile_private_or_missing', 'schema_retry_failed', 'missing_arabic', 'site_unreachable', 'empty_brain_context', 'manual_review',
] as const;

export const ANALYSIS_METHODS = ['ai_analyst', 'deterministic_fallback'] as const;

// ---------- root ----------
export const AuditAnalysisSchema = z.object({
  version: z.literal(AUDIT_ANALYSIS_VERSION),
  auditId: z.string().min(1).max(64),
  generatedAt: z.string().datetime(),
  locale: z.enum(['en', 'ar']),
  subject: SubjectSchema,
  executiveSummary: LocalizedTextSchema,
  overall: Score.nullable(),
  signals: z.array(SignalAssessmentSchema).length(6),
  findings: z.array(FindingSchema).max(60),
  plan: PlanSchema,
  competitors: z.array(CompetitorAnalysisSchema).max(8),
  channels: z.array(ChannelSchema).max(20).default([]),
  kpiTargets: z.array(KpiTargetSchema).max(20).default([]),
  coverage: CoverageSchema,
  qaStatus: z.enum(QA_STATUSES),
  qaReasons: z.array(z.enum(QA_HOLD_REASONS)).max(QA_HOLD_REASONS.length),
  method: z.enum(ANALYSIS_METHODS),
  /** Model id actually used (e.g. 'claude-sonnet-5'); null for deterministic fallback. */
  model: z.string().max(100).nullable(),
  /** Hash of the Brain pack(s) given as context; null when no Brain context. */
  brainPackHash: z.string().max(128).nullable(),
}).superRefine((a, ctx) => {
  const ids = a.signals.map((s) => s.id);
  for (const id of SIGNAL_IDS) {
    if (ids.filter((x) => x === id).length !== 1) ctx.addIssue({ code: 'custom', path: ['signals'], message: `signal '${id}' must appear exactly once` });
  }
  const findingIds = new Set<string>();
  a.findings.forEach((f, i) => {
    if (findingIds.has(f.id)) ctx.addIssue({ code: 'custom', path: ['findings', i, 'id'], message: `duplicate finding id '${f.id}'` });
    findingIds.add(f.id);
  });
  (['quickWins', 'days30', 'days90'] as const).forEach((bucket) => {
    a.plan[bucket].forEach((ref, i) => {
      if (!findingIds.has(ref)) ctx.addIssue({ code: 'custom', path: ['plan', bucket, i], message: `unknown finding id '${ref}'` });
    });
  });
  if (a.qaStatus === 'pass' && a.qaReasons.length > 0) ctx.addIssue({ code: 'custom', path: ['qaReasons'], message: 'qaReasons must be empty when qaStatus is pass' });
  if (a.qaStatus === 'hold' && a.qaReasons.length === 0) ctx.addIssue({ code: 'custom', path: ['qaReasons'], message: 'hold requires at least one reason' });
  if (a.method === 'deterministic_fallback' && a.qaStatus !== 'hold') ctx.addIssue({ code: 'custom', path: ['qaStatus'], message: 'fallback output must be held for review' });
  if (a.method === 'ai_analyst' && !a.model) ctx.addIssue({ code: 'custom', path: ['model'], message: 'model required for ai_analyst' });
});
export type AuditAnalysis = z.infer<typeof AuditAnalysisSchema>;

/** Collects customer-facing texts missing Arabic; QA uses this for Arabic-locale deliveries. */
export function missingArabic(a: AuditAnalysis): string[] {
  const out: string[] = [];
  const check = (t: LocalizedText | null, path: string) => { if (t && t.ar === null) out.push(path); };
  check(a.executiveSummary, 'executiveSummary');
  a.signals.forEach((s) => { check(s.headline, `signals.${s.id}.headline`); check(s.whatWeFound, `signals.${s.id}.whatWeFound`); check(s.whyItMatters, `signals.${s.id}.whyItMatters`); });
  a.findings.forEach((f) => {
    for (const k of ['title', 'evidence', 'impact', 'fix'] as const) check(f[k], `findings.${f.id}.${k}`);
    f.steps.forEach((s, i) => check(s, `findings.${f.id}.steps.${i}`));
  });
  a.competitors.forEach((c, i) => check(c.edge, `competitors.${i}.edge`));
  a.channels.forEach((c) => check(c.whatWeFound, `channels.${c.channel}.whatWeFound`));
  a.kpiTargets.forEach((k, i) => check(k.metric, `kpiTargets.${i}.metric`));
  return out;
}

// ---------- Brain context (Track B produces via packages/ai loadBrainContext; Track A consumes) ----------
export const BrainContextSchema = z.object({
  /** Pack slugs served, e.g. ['healthcare', 'm-optimization']. Empty = no sector pack matched. */
  packs: z.array(z.string().max(64)).max(20),
  /** Industry slug the job was mapped to, or null when unmapped. */
  industry: z.string().max(64).nullable(),
  /** Prompt-ready context text (bounded). */
  text: z.string().max(60000),
  /** Row refs the analyst may cite in Finding.brainRefs. */
  refs: z.array(z.object({ ref: Ref, kind: z.string().max(40), title: z.string().max(300), evidenceLevel: z.string().max(40) })).max(400),
  groundedRatio: z.number().min(0).max(1),
  packHash: z.string().max(128).nullable(),
});
export type BrainContext = z.infer<typeof BrainContextSchema>;
export const EMPTY_BRAIN_CONTEXT: BrainContext = { packs: [], industry: null, text: '', refs: [], groundedRatio: 0, packHash: null };
